import jwt_decode from 'jwt-decode'
import React from 'react'
import { useEffect, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom'
import './App.scss'
import ProtectedRoute from './auth/ProtectedRoute'
import AccessDenied from './components/UI/AccessDenied/AccessDenied'
import Loading from './components/UI/Loading/Loading'
import { appRoutes } from './config/app.routes'
import { GlobalContext } from './context/GlobalContext'
import ChooseAgent from './routes/ChooseAgent/ChooseAgent'
import { getAllAgents } from './store/agents/actions'
import { processAuthState, resetState } from './store/auth/actions'
import { IsLoggedIn } from './store/auth/selector'
import {
  getAllOrganisations,
  getOrganizationCurrentUserDetails,
  getOrganizationStates,
} from './store/common/actions'
import history from './utils/history'
import { useAuth0 } from './utils/react-auth0-wrapper'
import MainLayout from './components/MainLayout/MainLayout'
import { USER_REGISTRATION } from './config'
import AppInstance from './axios-global'
import DataApiInstance from './axios-data-instance'
import { ThemeProvider } from '@mui/material'
import { theme } from './theme'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import dayjs from 'dayjs'

import utc from 'dayjs/plugin/utc'
import { removeDeprecatedToken, setToken } from './utils/tokenStorage'
dayjs.extend(utc)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
})

const UserRegistration = React.lazy(
  () => import('./routes/UserRegistration/UserRegistration'),
)

const App: React.FC = () => {
  const { isAuthenticated, loading } = useAuth0()
  const dispatch = useDispatch()
  const loggedIn = useSelector(IsLoggedIn)

  const [loadingFromApp, setLoadingFromApp] = useState(false)
  const [isOneAuthFlowStarted, setOneAuthFlowStarted] = useState(false)
  const [currentOrganisation, setCurrentOrganisation] = useState(null)
  const [accessDeniedMode, setAccessDeniedMode] = useState(false)
  const [activateChooseAgentScreen, setActivateChooseAgentScreen] =
    useState(false)
  const [allAgents, setAllAgents] = useState([])
  const [showChangeOrganisation, setShowChangeOrganisation] = useState(false)
  const [allOrganisations, setAllOrganisations] = useState([])

  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem('viewMode') || '',
  )

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem('viewMode')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  const [selectedAgentName, setSelectedAgentName] = useState(
    localStorage.getItem('selectedAgentName'),
  )
  const [selectedAgentEmail, setSelectedAgentEmail] = useState(
    localStorage.getItem('selectedAgentEmail'),
  )

  const [sessionsData, setSessionsData] = useState([])

  // state used while exporting questions
  const [exportedQuestions, setExportQuestions] = useState(null)

  // header text - can be overridden by components
  const [headerText, setHeaderText] = useState<string | null>(null)

  const windowObj: any = window
  /**
   *
   *
   */
  const getOrgStates = async () => {
    // if (isAuthenticated || loggedIn) {
    await dispatch(getOrganizationStates())
    await Promise.all([
      setOneAuthFlowStarted(true), // used for loading purpose only nothing to do with business logic,
      dispatch(getAllAgents()),
    ])
      .then(async allAgents => {
        const agents: any = allAgents[1]
        await setAllAgents(agents)
        if (!agents || agents.length === 0) {
          await setActivateChooseAgentScreen(true)
          await setOneAuthFlowStarted(false)
          return
        }
        return Promise.all([dispatch(getOrganizationStates())])
          .then(async (organization: any) => {
            const stateRecord: any = organization[0]
            setSelectedAgentName(stateRecord.selectedAgentName || '')
            setSelectedAgentEmail(stateRecord.selectedAgentName || '')
            dispatch(getOrganizationCurrentUserDetails())

            const hasAgent = agents.find(
              (agent: any) => agent._id === stateRecord.selectedAgent,
            )

            if (
              !stateRecord.hasOwnProperty('selectedAgent') ||
              !stateRecord.selectedAgent ||
              !hasAgent
            ) {
              setActivateChooseAgentScreen(true)
            }
            setOneAuthFlowStarted(false)
            setLoadingFromApp(false)
          })
          .catch(error => {
            setOneAuthFlowStarted(false)
          })
      })
      .catch(err => {
        setOneAuthFlowStarted(false)
      })
    // }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const backUrl = localStorage.getItem('originalPath')

    // Remove deprecated token from local storage
    removeDeprecatedToken()

    dispatch(resetState())
    let code, state, error
    // Function to initiate one auth flow based on query params
    const processAuthFn = async (code: string) => {
      setOneAuthFlowStarted(true)
      const token: any = await dispatch(processAuthState(code))
      if (token) {
        setToken(token)
        await localStorage.removeItem('originalPath')
        await Promise.all([
          dispatch(getOrganizationStates()), // used for loading purpose only nothing to do with business logic,
          dispatch(getAllAgents()),
          dispatch(getAllOrganisations()),
        ])
          .then(async res => {
            const stateRecord: any = res[0]
            const agents: any = res[1]
            const orgs: any = res[2]
            await setAllAgents(agents)
            await setAllOrganisations(orgs.organisations || [])
            if (res && orgs.organisations && orgs.organisations.length > 1) {
              await setShowChangeOrganisation(true)
            }
            if (!agents || agents.length === 0) {
              await setActivateChooseAgentScreen(true)
            }

            setSelectedAgentName(stateRecord.selectedAgentName || '')
            setSelectedAgentEmail(stateRecord.selectedAgentName || '')
            dispatch(getOrganizationCurrentUserDetails())

            const hasAgent = agents.find(
              (agent: any) => agent._id === stateRecord.selectedAgent,
            )

            if (
              !stateRecord.hasOwnProperty('selectedAgent') ||
              !stateRecord.selectedAgent ||
              !hasAgent
            ) {
              setActivateChooseAgentScreen(true)
            }
          })
          .catch(err => {
            console.log('AUTH ERR', err)
          })
        history.push(backUrl)
        localStorage.removeItem('originalPath')
      }
      setOneAuthFlowStarted(false)
    }

    // If query params exist from one auth
    if (history.location && history.location.search) {
      const query = new URLSearchParams(history.location.search)
      code = query.get('code')
      state = query.get('state')
      error = query.get('error')
      const viewModeParam = query.get('viewMode') || ''
      if (viewModeParam) {
        localStorage.setItem('viewMode', viewModeParam)
        setViewMode(viewModeParam)
      }
      if (windowObj.REACT_APP_SSO_PROVIDER === 'oneauth' && code && state) {
        if (!error) {
          // call authentication API for oneauth
          processAuthFn(code)
        }
        // Check for errors
        if (error && (error === 'unauthorized' || error === 'access_denied')) {
          setAccessDeniedMode(true)
        }
      }
      const token = query.get('t')
      if (token) {
        AppInstance.defaults.headers.common.Authorization = `Bearer ${token}`
        DataApiInstance.defaults.headers.common.Authorization = `Bearer ${token}`
        getOrgStates()
      }
    }

    if (isAuthenticated) {
      setOneAuthFlowStarted(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (windowObj.REACT_APP_SSO_PROVIDER === 'oneauth') {
      return
    }
    if (!loadingFromApp) {
      setLoadingFromApp(isOneAuthFlowStarted || loading || loggedIn)
    }

    if (!isOneAuthFlowStarted && !loading && !loggedIn && loadingFromApp) {
      setLoadingFromApp(false)
    }

    return () => {}
  }, [isOneAuthFlowStarted, loading, loggedIn])

  useEffect(() => {
    if (isOneAuthFlowStarted) {
      return
    }

    const getOrganizationStatesFn = async () => {
      if (isAuthenticated || loggedIn) {
        await dispatch(getOrganizationStates())
        const getOrgList = async () => {
          const res: any = await dispatch(getAllOrganisations())
          setAllOrganisations(res.organisations || [])
          if (res && res.organisations && res.organisations.length > 1) {
            setShowChangeOrganisation(true)
          }
        }
        getOrgList()
        await Promise.all([
          setOneAuthFlowStarted(true), // used for loading purpose only nothing to do with business logic,
          dispatch(getAllAgents()),
        ])
          .then(async allAgents => {
            const agents: any = allAgents[1]
            await setAllAgents(agents)
            if (!agents || agents.length === 0) {
              await setActivateChooseAgentScreen(true)
              await setOneAuthFlowStarted(false)
              return
            }
            return Promise.all([dispatch(getOrganizationStates())])
              .then(async (organization: any) => {
                const stateRecord: any = organization[0]
                setSelectedAgentName(stateRecord.selectedAgentName || '')
                setSelectedAgentEmail(stateRecord.selectedAgentName || '')
                dispatch(getOrganizationCurrentUserDetails())

                const hasAgent = agents.find(
                  (agent: any) => agent._id === stateRecord.selectedAgent,
                )

                if (
                  !stateRecord.hasOwnProperty('selectedAgent') ||
                  !stateRecord.selectedAgent ||
                  !hasAgent
                ) {
                  setActivateChooseAgentScreen(true)
                }

                setOneAuthFlowStarted(false)
              })
              .catch(error => {
                setOneAuthFlowStarted(false)
              })
          })
          .catch(err => {
            setOneAuthFlowStarted(false)
          })
      }
    }
    if (!isOneAuthFlowStarted) {
      getOrganizationStatesFn()
    }
  }, [isAuthenticated, loggedIn]) // auth0 and oneauth

  const agentSelectedHandler = (e: any) => {
    setActivateChooseAgentScreen(false)
  }

  // Access denied page
  if (accessDeniedMode) {
    return <AccessDenied />
  }

  // Loading screen
  if (loadingFromApp && windowObj.REACT_APP_SSO_PROVIDER !== 'oneauth') {
    return <Loading />
  }

  if (isOneAuthFlowStarted && windowObj.REACT_APP_SSO_PROVIDER === 'oneauth') {
    return <Loading />
  }

  // Choose agent screen
  if (activateChooseAgentScreen) {
    return (
      <GlobalContext.Provider
        value={{
          viewMode,
          accessDeniedMode,
          setAccessDeniedMode,
          selectedAgentName,
          selectedAgentEmail,
          setSelectedAgentName,
          setSelectedAgentEmail,
          showChangeOrganisation,
          allOrganisations,
          currentOrganisation,
          setCurrentOrganisation,
          exportedQuestions,
          setExportQuestions,
          sessionsData,
          setSessionsData,
          headerText,
          setHeaderText,
        }}
      >
        <ChooseAgent
          allAgents={allAgents}
          agentSelected={agentSelectedHandler}
        />
      </GlobalContext.Provider>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalContext.Provider
        value={{
          viewMode,
          accessDeniedMode,
          setAccessDeniedMode,
          selectedAgentName,
          selectedAgentEmail,
          setSelectedAgentName,
          setSelectedAgentEmail,
          showChangeOrganisation,
          allOrganisations,
          currentOrganisation,
          setCurrentOrganisation,
          exportedQuestions,
          setExportQuestions,
          sessionsData,
          setSessionsData,
          headerText,
          setHeaderText,
        }}
      >
        <ThemeProvider theme={theme}>
          <Router basename={windowObj.SUB_DOMAIN || '/admin'}>
            <React.Suspense fallback={<Loading />}>
              <Toaster containerStyle={{ top: '77px' }} />
              <Switch>
                {!activateChooseAgentScreen &&
                  !isOneAuthFlowStarted &&
                  appRoutes.map((routes: any, index: number) => {
                    return (
                      <ProtectedRoute
                        key={'routing_index_' + index}
                        history={history}
                        exact
                        {...routes}
                      />
                    )
                  })}
                <Route
                  exact
                  path={USER_REGISTRATION}
                  render={matchProps => (
                    <MainLayout showSidebar={false} showHeader={false}>
                      <UserRegistration history={history} {...matchProps} />
                    </MainLayout>
                  )}
                />
              </Switch>
            </React.Suspense>
          </Router>
        </ThemeProvider>
      </GlobalContext.Provider>
    </QueryClientProvider>
  )
}

export default App
